<template>
  <ContentWrap>
    <el-alert
      class="mb-16px"
      type="info"
      :closable="false"
      title="配置商家入驻套餐：价格为 0 即免费档；商品上限填 -1 表示不限。C 端申请时自选档位，付费档先支付再进审核。"
    />
    <div class="mb-12px">
      <el-button type="primary" v-hasPermi="['merchant:plan:create']" @click="openCreate">
        <Icon icon="ep:plus" />新增套餐
      </el-button>
    </div>
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column label="名称" prop="name" min-width="140" />
      <el-table-column label="等级序" prop="level" width="90" align="center" />
      <el-table-column label="排序" prop="sort" width="80" align="center" />
      <el-table-column label="价格（元）" min-width="120">
        <template #default="{ row }">{{ fenToYuan(row.price) }}</template>
      </el-table-column>
      <el-table-column label="商品上限" width="110" align="center">
        <template #default="{ row }">
          {{ row.spuLimit === -1 || row.spuLimit == null ? '不限' : row.spuLimit }}
        </template>
      </el-table-column>
      <el-table-column label="状态" width="90" align="center">
        <template #default="{ row }">
          <el-tag :type="row.status === 0 ? 'success' : 'info'" size="small">
            {{ row.status === 0 ? '启用' : '停用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="备注" prop="remark" min-width="160" show-overflow-tooltip />
      <el-table-column label="操作" width="160" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" v-hasPermi="['merchant:plan:update']" @click="openEdit(row)">
            编辑
          </el-button>
          <el-button link type="danger" v-hasPermi="['merchant:plan:delete']" @click="handleDelete(row)">
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <Dialog v-model="formVisible" :title="formMode === 'create' ? '新增入驻套餐' : '编辑入驻套餐'" width="520px">
    <el-form ref="formRef" :model="form" :rules="rules" label-width="120px">
      <el-form-item label="套餐名称" prop="name">
        <el-input v-model="form.name" maxlength="64" placeholder="如：免费入驻 / 金牌商家" />
      </el-form-item>
      <el-form-item label="等级序" prop="level">
        <el-input-number v-model="form.level" :min="1" class="!w-100%" />
        <el-text size="small" type="info">越大越高，升档只能升到更高等级</el-text>
      </el-form-item>
      <el-form-item label="展示排序" prop="sort">
        <el-input-number v-model="form.sort" :min="0" class="!w-100%" />
      </el-form-item>
      <el-form-item label="价格（元）" prop="priceYuan">
        <el-input-number v-model="form.priceYuan" :min="0" :precision="2" :step="100" class="!w-100%" />
      </el-form-item>
      <el-form-item label="商品上限" prop="spuLimit">
        <el-input-number v-model="form.spuLimit" :min="-1" class="!w-100%" />
        <el-text size="small" type="info">-1 表示不限</el-text>
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-radio-group v-model="form.status">
          <el-radio :value="0">启用</el-radio>
          <el-radio :value="1">停用</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="说明">
        <el-input v-model="form.remark" type="textarea" :rows="2" maxlength="255" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="formVisible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="submitForm">保存</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import * as PlanApi from '@/api/merchant/plan'
import type { MerchantPlanVO } from '@/api/merchant/plan'

defineOptions({ name: 'MallMerchantPlan' })

const message = useMessage()
const loading = ref(false)
const saving = ref(false)
const list = ref<MerchantPlanVO[]>([])
const total = ref(0)
const queryParams = reactive({ pageNo: 1, pageSize: 20 })
const formVisible = ref(false)
const formMode = ref<'create' | 'edit'>('create')
const formRef = ref()
const form = reactive({
  id: undefined as number | undefined,
  name: '',
  level: 1,
  sort: 0,
  priceYuan: 0,
  spuLimit: 10,
  status: 0,
  remark: ''
})

const rules = {
  name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
  level: [{ required: true, message: '请输入等级序', trigger: 'blur' }],
  priceYuan: [{ required: true, message: '请输入价格', trigger: 'blur' }],
  spuLimit: [{ required: true, message: '请输入商品上限', trigger: 'blur' }],
  status: [{ required: true, message: '请选择状态', trigger: 'change' }]
}

const fenToYuan = (fen?: number) => ((Number(fen || 0) / 100) || 0).toFixed(2)

const getList = async () => {
  loading.value = true
  try {
    const data = await PlanApi.getPlanPage(queryParams)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const resetForm = () => {
  form.id = undefined
  form.name = ''
  form.level = 1
  form.sort = 0
  form.priceYuan = 0
  form.spuLimit = 10
  form.status = 0
  form.remark = ''
}

const openCreate = () => {
  formMode.value = 'create'
  resetForm()
  formVisible.value = true
}

const openEdit = (row: MerchantPlanVO) => {
  formMode.value = 'edit'
  form.id = row.id
  form.name = row.name
  form.level = row.level
  form.sort = row.sort ?? 0
  form.priceYuan = Number(row.price || 0) / 100
  form.spuLimit = row.spuLimit ?? 10
  form.status = row.status
  form.remark = row.remark || ''
  formVisible.value = true
}

const submitForm = async () => {
  await formRef.value?.validate()
  saving.value = true
  try {
    const payload: MerchantPlanVO = {
      id: form.id,
      name: form.name,
      level: form.level,
      sort: form.sort,
      price: Math.round(Number(form.priceYuan || 0) * 100),
      spuLimit: form.spuLimit,
      status: form.status,
      remark: form.remark
    }
    if (formMode.value === 'create') {
      await PlanApi.createPlan(payload)
      message.success('创建成功')
    } else {
      await PlanApi.updatePlan(payload)
      message.success('更新成功')
    }
    formVisible.value = false
    await getList()
  } finally {
    saving.value = false
  }
}

const handleDelete = async (row: MerchantPlanVO) => {
  await message.delConfirm(`确认删除套餐「${row.name}」？`)
  await PlanApi.deletePlan(row.id!)
  message.success('已删除')
  await getList()
}

onMounted(getList)
</script>
